import React, { useState, useEffect, Fragment, useMemo, useRef } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics/ai-events-instrumentation-provider';
import type { AIEventsInstrumentationConfig } from '@atlassian/ai-analytics/ai-events-instrumentation-context';

import { AIEmojiSuggestionForTitleGenBridge } from '@confluence/emoji-title/entry-points/AIEmojiSuggestionForTitleGenBridge.ts';
import { EmojiTitleStateContainer } from '@confluence/emoji-title/entry-points/emojiTitleStateContainer.ts';
import { EditorTitle } from '@confluence/editor-title';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { TitleAlignmentType } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { useTitleContentPropertiesForEditor } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForEditor.ts';
import { CoverPictureStateContainer } from '@confluence/page-cover-picture/entry-points/CoverPictureStateContainer.ts';
import { useHeaderImageGeneration } from '@confluence/page-cover-picture/entry-points/useHeaderImageGeneration.ts';
import { LivePagesTitle } from '@confluence/editor-title/entry-points/LivePagesTitle.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useGeneratedTitle } from '@confluence/title-area-utils/entry-points/useGeneratedTitle.ts';
import { useImageGenerationCallbacks } from '@confluence/title-area-utils/entry-points/useImageGenerationCallbacks.ts';
import { expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	ONBOARDING_EXPERIENCES,
	NTH_USER_EMOJI_TITLE_DISCOVERED,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { isLocaleEnglish } from '@confluence/onboarding-helpers/entry-points/isLocaleEnglish.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useMeetingSignalDetection } from '@confluence/experiment-meeting-signal-instrumentation/entry-points/useMeetingSignalDetection.ts';
import { EditorTitleWithTitleAreaComponents } from './EditorTitleWithTitleAreaComponents.tsx';
import type { TitleAreaContentComponentsProps } from './titleareaContentComponentsTypes.ts';
import { AlignmentButtonContainer } from './AlignmentButtonContainer.tsx';

const i18n = defineMessages({
	aiBlankPagePlaceholder: {
		id: 'title-area-content-components.ai-blank-page-placeholder',
		defaultMessage: 'New page',
		description: 'Placeholder text for a new blank page title in the full-page editor',
	},
	aiBlankLiveDocPlaceholder: {
		id: 'title-area-content-components.ai-blank-live-doc-placeholder',
		defaultMessage: 'New live doc',
		description: 'Placeholder text for a new blank live doc title in the full-page editor',
	},
	autoPageEmojiPlaceholderTitle: {
		id: 'title-area-content-components.title-section.auto-page-emoji-placeholder-title',
		defaultMessage: 'Start with a title and emoji',
		description:
			'placeholder title for users enrolled in the auto page emoji experiment prompting them to add a page title and emoji.',
	},
});

const topLineBoxStylesMarginTopSpaceNegative300 = xcss({
	marginTop: 'space.negative.300',
});

const breadcrumbAndTitleGapStyles = xcss({
	marginBottom: 'space.300',
});

const aiAnalyticsConfig: AIEventsInstrumentationConfig = {
	product: 'confluence',
	subproduct: 'confluence',
	source: 'confluencePage',
	aiFeatureName: 'aiTitleGenOnPublish',
	proactiveGeneratedAI: 1,
	userGeneratedAI: 0,
};

type TitleAreaComponentWithState = TitleAreaContentComponentsProps & {
	alignmentButtonContainer: AlignmentButtonContainer;
	coverPictureStateContainer: CoverPictureStateContainer;
	flags: FlagsStateContainer;
	contentComponentsDisabled?: boolean;
	isDraftLockedForEditing?: boolean;
	isStatusDisconnected?: boolean;
	pageAlreadyHasEmoji?: boolean;
};

const TitleAreaComponentWithState = ({
	title,
	breadcrumbsComponent,
	contentId,
	cloudId,
	isDisabled,
	linkCreation,
	onChange,
	focusEditor,
	onFocusChanged,
	debounceOnChange,
	contentType,
	userOffline,
	isInPreview,
	isUnpublishedDraft,
	isPublishingContent,
	templateSuggestionsRef,
	templateButtonRef,
	isTemplateEditor = false,
	contentTopperComponent: headerActionsComponent,
	viewMode,
	contentClassificationMetadata,
	version,
	isEditorEmpty,
	collabEditInitialized,
	alignmentButtonContainer,
	coverPictureStateContainer,
	flags,
	contentComponentsDisabled,
	isDraftLockedForEditing,
	isStatusDisconnected,
	editorApi,
	editorActions,
	editorSessionId,
	isSeamlessTransitionInProgress = false,
	isTitleGenerating,
	onTitleGeneratingChange,
	isEmbeddedConfluence,
	customPlaceholderOverride,
	isDisabledTextBlack,
	pageAlreadyHasEmoji,
}: TitleAreaComponentWithState) => {
	const spaceId = useSpaceId();
	const {
		cloudId: sessionCloudId,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
		userId,
	} = useSessionData();
	const { formatMessage, locale } = useIntl();
	const hasTitleChangedRef = useRef<boolean>(false);
	const detectMeetingSignal = useMeetingSignalDetection();
	const hasTitleSuggestionTriggeredRef = useRef<boolean>(false);
	const updateAlignmentState = alignmentButtonContainer?.toggleAlignmentButton;
	const updateCoverPictureWidthState = coverPictureStateContainer?.toggleCoverPictureWidthButton;
	const updateIsLoadingComplete = alignmentButtonContainer?.setIsLoadingComplete;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const hideWhileLoading = !(window.__SSR_RENDERED__ || process.env.REACT_SSR);
	const { isTitleAlignmentLoading, isTitleCenteredInitial, coverImageWidthCached } =
		useTitleContentPropertiesForEditor({
			contentId,
			updateAlignmentState,
			updateCoverPictureWidthState,
			loadTitleContentProperties: true,
			contentType,
			flags,
			updateIsLoadingComplete,
		});

	const [localUserOffline, setLocalUserOffline] = useState<boolean>(false);
	const [suppressTitleFocus, setSuppressTitleFocus] = useState(false);
	const hasActivatedBoeFocusRef = useRef(false);
	const isLivePage = useIsCurrentPageLive();
	const [spaceKey = ''] = usePageSpaceKey();
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, action] = useGeneratedTitle();

	const [titleAfterBlur, setTitleAfterBlur] = useState<string | null>(null);
	const onTitleBlur = (currentTitle: string | undefined) => {
		hasTitleSuggestionTriggeredRef.current = true;
		if (isLivePage && fg('cc_fd_suggest_whiteboards_on_page_title_gate')) {
			hasTitleChangedRef.current = true;
		}
		const resolvedTitle = currentTitle ?? title;
		setTitleAfterBlur(resolvedTitle);
		// Meeting-signal instrumentation (STARB-3763)
		detectMeetingSignal({
			pageType: isLivePage ? 'livePage' : 'page',
			trigger: 'titleEdit',
			titleText: resolvedTitle,
		});
	};

	const isEnglishLocale = isLocaleEnglish(locale);
	const isEligibleForAutoPageEmojiAudienceCheck =
		isAdminHubAIEnabled &&
		isEnglishLocale &&
		(contentType === 'page' || contentType === 'blogpost');

	const { data: onboardingStateData } = useGetOnboardingState(
		[
			ONBOARDING_EXPERIENCES.NTH_USER_ONBOARDING_COMPLETED_TIMESTAMP,
			NTH_USER_EMOJI_TITLE_DISCOVERED.EMOJI_TITLE_DISCOVERED,
		],
		!isEligibleForAutoPageEmojiAudienceCheck,
	);
	const { nthUserOnboardingCompletedTimestamp, emojiTitleDiscovered } =
		deserializeState(onboardingStateData);
	const completedTimestamp = parseInt(nthUserOnboardingCompletedTimestamp, 10) ?? 0;

	const isAutoPageEmojiEligible =
		isEligibleForAutoPageEmojiAudienceCheck && completedTimestamp >= Date.parse('2025-12-04');

	const customPlaceholder = useMemo(() => {
		if (customPlaceholderOverride) {
			return customPlaceholderOverride;
		}

		if (
			!isEmbeddedConfluence &&
			isRovoLLMEnabled &&
			isExperimentEnabled('cwr_blank_object_experiment') &&
			contentType !== 'blogpost'
		) {
			return formatMessage(
				isLivePage ? i18n.aiBlankLiveDocPlaceholder : i18n.aiBlankPagePlaceholder,
			);
		}

		if (isAutoPageEmojiEligible) {
			return formatMessage(i18n.autoPageEmojiPlaceholderTitle);
		}

		return undefined;
	}, [
		isEmbeddedConfluence,
		isLivePage,
		contentType,
		isAutoPageEmojiEligible,
		formatMessage,
		customPlaceholderOverride,
		isRovoLLMEnabled,
	]);

	const contentClassificationFromHook = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: isLivePage ? 'current-and-draft' : 'draft-only',
	});

	const contentClassification = !contentClassificationMetadata
		? contentClassificationFromHook
		: contentClassificationMetadata;

	const { shouldDisplayClassification } = contentClassification;

	// Experience tracking callbacks for AI image generation
	const imageGenerationCallbacks = useImageGenerationCallbacks();

	const {
		generateImage,
		aiHeaderImageStatus,
		aiHeaderImageFileId,
		setAIHeaderImageStatus,
		error: aiHeaderImageError,
	} = useHeaderImageGeneration({
		cloudId: cloudId ?? sessionCloudId,
		contentId,
		eventCallbacks: imageGenerationCallbacks,
		userId,
	});

	const onSuggestTitleClickedRef = useRef<(() => void) | undefined>(undefined);

	// Connection banner height has a delay to appear, adjust the draft banner height to reflect this delay
	useEffect(() => {
		const DELAY_TIMEOUT = 3000;
		const delayTimeout: ReturnType<typeof setTimeout> = setTimeout(
			() => setLocalUserOffline(userOffline),
			DELAY_TIMEOUT,
		);

		return () => {
			clearTimeout(delayTimeout);
		};
	}, [userOffline]);

	useEffect(() => {
		if (hasActivatedBoeFocusRef.current || isLivePage) {
			return;
		}
		if (
			collabEditInitialized &&
			isEditorEmpty &&
			!title &&
			(expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') ||
				expValEquals('cwr_blank_object_experiment', 'cohort', 'variant2'))
		) {
			hasActivatedBoeFocusRef.current = true;
			setSuppressTitleFocus(true);
			focusEditor?.();
		}
	}, [collabEditInitialized, focusEditor, isEditorEmpty, isLivePage, title]);

	const handleOnChangeWrapperForSuggestWhiteboardExperiment = (value: string) => {
		hasTitleChangedRef.current = true;
		onChange(value);
	};

	const renderTitleComponent = () => {
		if (isTitleAlignmentLoading && hideWhileLoading) {
			return null;
		}
		return (
			<Subscribe to={[FlagsStateContainer, AlignmentButtonContainer]}>
				{(flags: FlagsStateContainer, alignmentButtonContainer: AlignmentButtonContainer) => {
					const isTitleCenterAligned = !alignmentButtonContainer.state.isLoadingComplete
						? isTitleCenteredInitial
						: alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.CENTER;

					return (
						<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
							{isLivePage ? (
								<LivePagesTitle
									title={title ?? ''}
									contentId={contentId}
									cloudId={cloudId}
									userOffline={userOffline}
									isDisabled={isDisabled}
									isEditorEmpty={isEditorEmpty}
									focusEditor={focusEditor}
									onFocusChanged={onFocusChanged}
									onTitleBlur={onTitleBlur}
									viewMode={viewMode}
									isTitleCenterAligned={isTitleCenterAligned}
									version={version}
									editorApi={editorApi}
									editorActions={editorActions}
									editorSessionId={editorSessionId}
									isSeamlessTransitionInProgress={isSeamlessTransitionInProgress}
									customPlaceholder={customPlaceholder}
									flags={flags}
									onTitleGeneratingChange={onTitleGeneratingChange}
								/>
							) : (
								<EditorTitle
									title={title}
									contentId={contentId}
									cloudId={cloudId}
									isDisabled={isDisabled}
									suppressTitleFocus={suppressTitleFocus}
									onChange={
										fg('cc_fd_suggest_whiteboards_on_page_title_gate')
											? handleOnChangeWrapperForSuggestWhiteboardExperiment
											: onChange
									}
									focusEditor={focusEditor}
									onFocusChanged={onFocusChanged}
									setGeneratedTitle={action.setGeneratedTitle}
									setTrackAIResultViewStored={action.setTrackAIResultViewStored}
									onTitleBlur={onTitleBlur}
									debounceOnChange={debounceOnChange}
									contentType={contentType}
									userOffline={userOffline}
									isInPreview={isInPreview}
									isTemplateEditor={isTemplateEditor}
									templateSuggestionsRef={templateSuggestionsRef}
									templateButtonRef={templateButtonRef}
									flagArray={flags?.state?.flags ?? []}
									isTitleCenterAligned={isTitleCenterAligned}
									isUnpublishedDraft={isUnpublishedDraft}
									contentClassificationMetadata={contentClassification}
									hasClassification={shouldDisplayClassification}
									isEditMode
									isEditorEmpty={isEditorEmpty}
									spaceKey={spaceKey}
									spaceId={spaceId}
									editorApi={editorApi}
									editorSessionId={editorSessionId}
									isSeamlessTransitionInProgress={isSeamlessTransitionInProgress}
									isTitleGeneratingFromPaste={isTitleGenerating}
									onTitleGeneratingChange={onTitleGeneratingChange}
									customPlaceholder={customPlaceholder}
									isDisabledTextBlack={isDisabledTextBlack}
								/>
							)}
						</AIEventsInstrumentationProvider>
					);
				}}
			</Subscribe>
		);
	};

	return (
		<Fragment>
			{isTemplateEditor && (
				<Box
					xcss={[
						localUserOffline && topLineBoxStylesMarginTopSpaceNegative300,
						breadcrumbAndTitleGapStyles,
					]}
					testId="breadcrumbs-wrapper"
				>
					{breadcrumbsComponent}
				</Box>
			)}
			<EditorTitleWithTitleAreaComponents
				contentId={contentId}
				isPublishingContent={isPublishingContent}
				contentType={contentType}
				userOffline={userOffline}
				isTemplateEditor={isTemplateEditor}
				isTitleDisabled={isDisabled}
				isUnpublishedDraft={isUnpublishedDraft}
				isTitleCenterAligned={
					!alignmentButtonContainer.state.isLoadingComplete
						? isTitleCenteredInitial
						: alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.CENTER
				}
				linkCreation={linkCreation}
				viewMode={viewMode}
				contentTopperComponent={!isTemplateEditor && headerActionsComponent}
				contentClassificationMetadata={contentClassification}
				hasClassification={shouldDisplayClassification}
				spaceKey={spaceKey}
				isEditorEmpty={isEditorEmpty}
				isTitleAlignmentLoading={isTitleAlignmentLoading && hideWhileLoading}
				contentComponentsDisabled={contentComponentsDisabled}
				isDraftLockedForEditing={isDraftLockedForEditing}
				isStatusDisconnected={isStatusDisconnected}
				coverPictureState={coverPictureStateContainer}
				title={title}
				titleAfterBlur={titleAfterBlur}
				isLivePage={isLivePage}
				coverImageWidthCached={coverImageWidthCached}
				pageTitleContent={titleAfterBlur || undefined}
				aiHeaderImageStatus={
					isExperimentEnabled('cc_page_experiences_ai_header_image_generation')
						? aiHeaderImageStatus
						: undefined
				}
				aiHeaderImageFileId={
					isExperimentEnabled('cc_page_experiences_ai_header_image_generation')
						? aiHeaderImageFileId
						: undefined
				}
				generateAIHeaderImage={
					isExperimentEnabled('cc_page_experiences_ai_header_image_generation')
						? generateImage
						: undefined
				}
				setAIHeaderImageStatus={
					isExperimentEnabled('cc_page_experiences_ai_header_image_generation')
						? setAIHeaderImageStatus
						: undefined
				}
				aiHeaderImageError={
					isExperimentEnabled('cc_page_experiences_ai_header_image_generation')
						? aiHeaderImageError
						: undefined
				}
				isAutoPageEmojiEligible={isAutoPageEmojiEligible}
				emojiTitleDiscovered={emojiTitleDiscovered}
				onSuggestTitleClickedRef={onSuggestTitleClickedRef}
				version={version}
				hasTitleChangedRef={hasTitleChangedRef}
				hasTitleSuggestionTriggeredRef={hasTitleSuggestionTriggeredRef}
				isDisabledTextBlack={isDisabledTextBlack}
			>
				{renderTitleComponent()}
				<AIEmojiSuggestionForTitleGenBridge
					contentId={contentId}
					title={title}
					isTitleGenerating={isTitleGenerating}
					locale={locale}
					pageAlreadyHasEmoji={pageAlreadyHasEmoji}
					onSuggestTitleClickedRef={onSuggestTitleClickedRef}
				/>
			</EditorTitleWithTitleAreaComponents>
		</Fragment>
	);
};
TitleAreaComponentWithState.displayName = 'TitleAreaComponentWithState';

export const TitleAreaContentComponents: {
	(props: TitleAreaContentComponentsProps): React.JSX.Element;
	displayName: string;
} = (props: TitleAreaContentComponentsProps): React.JSX.Element => {
	return (
		<Subscribe
			to={[
				AlignmentButtonContainer,
				CoverPictureStateContainer,
				FlagsStateContainer,
				EmojiTitleStateContainer,
			]}
		>
			{(
				alignmentButtonContainer: AlignmentButtonContainer,
				coverPictureStateContainer: CoverPictureStateContainer,
				flags: FlagsStateContainer,
				emojiTitleState: EmojiTitleStateContainer,
			) => (
				<TitleAreaComponentWithState
					{...props}
					alignmentButtonContainer={alignmentButtonContainer}
					coverPictureStateContainer={coverPictureStateContainer}
					flags={flags}
					pageAlreadyHasEmoji={!!emojiTitleState.state.selectedEmoji}
				/>
			)}
		</Subscribe>
	);
};
TitleAreaContentComponents.displayName = 'TitleAreaContentComponents';
